// Studio mixer: an accordion of all diatoms. Each row collapses to a header with
// mute/solo; expands to per-diatom controls (volume, excitation source). The window has
// a master mute-all. Opened from the main panel. Monochrome to match the field.

const STUDIO_STYLE = `
.ds-studio {
  position: fixed; top: 1rem; right: 1rem; z-index: 8; width: 260px; max-height: 84vh;
  display: flex; flex-direction: column; border-radius: 12px; overflow: hidden;
  background: rgba(0,0,0,0.78); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.22); color: #ededed;
  font-family: system-ui, sans-serif; font-size: 0.72rem; letter-spacing: 0.06em;
  transition: opacity 0.3s ease, transform 0.3s ease; user-select: none;
}
.ds-studio.hidden { opacity: 0; pointer-events: none; transform: translateX(12px); }
.ds-studio-head {
  display: flex; align-items: center; gap: 0.5rem; padding: 0.7rem 0.8rem;
  border-bottom: 1px solid rgba(255,255,255,0.15); flex: 0 0 auto;
}
.ds-studio-title { flex: 1; text-transform: uppercase; letter-spacing: 0.2em; font-size: 0.7rem; }
/* flex:1 + min-height:0 makes THIS the scroll container; rows below keep their height */
.ds-studio-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 0.4rem; display: flex; flex-direction: column; gap: 0.3rem; }
.ds-acc-row { flex: 0 0 auto; border: 1px solid rgba(255,255,255,0.14); border-radius: 7px; overflow: hidden; }
.ds-acc-head { display: flex; align-items: center; gap: 0.35rem; padding: 0.35rem 0.4rem; min-height: 40px; box-sizing: border-box; }
.ds-acc-name { flex: 1; cursor: pointer; font-size: 0.66rem; }
.ds-mini {
  min-width: 26px; min-height: 26px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.28);
  background: transparent; color: #cfcfcf; cursor: pointer; font-size: 0.6rem; font-weight: 600;
}
.ds-mini:hover { border-color: #fff; }
.ds-mini.on-m { background: #c44; color: #fff; border-color: #c44; }
.ds-mini.on-s { background: #f2c14e; color: #000; border-color: #f2c14e; }
.ds-acc-body { display: none; flex-direction: column; gap: 0.4rem; padding: 0.4rem 0.5rem 0.55rem; border-top: 1px solid rgba(255,255,255,0.1); }
.ds-acc-row.open .ds-acc-body { display: flex; }
.ds-acc-body .ds-row label { opacity: 0.7; font-size: 0.6rem; text-transform: uppercase; }
.ds-acc-body select {
  background: #000; color: #fff; border: 1px solid rgba(255,255,255,0.28); border-radius: 5px;
  font-size: 0.62rem; padding: 0.25rem; -webkit-appearance: none; appearance: none;
}
.ds-studio-foot {
  display: flex; gap: 0.3rem; align-items: center; padding: 0.5rem 0.6rem;
  border-top: 1px solid rgba(255,255,255,0.15); flex: 0 0 auto;
}
.ds-scene-name {
  flex: 1; min-width: 0; background: #000; color: #fff; border: 1px solid rgba(255,255,255,0.28);
  border-radius: 5px; padding: 0.35rem 0.4rem; font-size: 0.62rem; font-family: inherit;
}
/* 16px form controls on touch -> no iOS focus-zoom. */
@media (pointer: coarse) { .ds-acc-body select, .ds-scene-name { font-size: 16px; } }
`;

let injected = false;
function inject(): void {
  if (injected) return;
  const el = document.createElement('style');
  el.textContent = STUDIO_STYLE;
  document.head.appendChild(el);
  injected = true;
}

function nameFor(id: string): string {
  const m = /^screenshot_(\d+)$/.exec(id);
  if (m) return `Diatom ${m[1]}`;
  return id.charAt(0).toUpperCase() + id.slice(1);
}

export type Excitation = 'auto' | 'strike' | 'bow';

export interface StudioCallbacks {
  onMute: (id: string, muted: boolean) => void;
  onSolo: (id: string, solo: boolean) => void;
  onGain: (id: string, gain: number) => void;
  onExcitation: (id: string, ex: Excitation) => void;
  onSaveScene: (name: string) => void;
  onExportScene: (name: string) => void;
  onImportScene: (file: File) => void;
}

export interface DiatomState {
  gain: number;
  excitation: Excitation;
  muted: boolean;
  soloed: boolean;
}

export interface StudioHandle {
  el: HTMLElement;
  toggle: () => void;
  isOpen: () => boolean;
  /** Expand + scroll to a diatom's row (used when its sprite is clicked). */
  reveal: (id: string) => void;
  /** Reflect external per-diatom state in the UI (used when loading a saved scene). */
  setState: (states: Record<string, DiatomState>) => void;
}

export function createStudioPanel(
  diatoms: ReadonlyArray<{ id: string }>,
  cb: StudioCallbacks,
): StudioHandle {
  inject();
  const panel = document.createElement('div');
  panel.className = 'ds-studio hidden';

  interface Entry {
    id: string;
    row: HTMLElement;
    mBtn: HTMLButtonElement;
    sBtn: HTMLButtonElement;
    vol: HTMLInputElement;
    ex: HTMLSelectElement;
  }
  const entries: Entry[] = [];
  const rowsById = new Map<string, HTMLElement>();

  const head = document.createElement('div');
  head.className = 'ds-studio-head';
  const title = document.createElement('div');
  title.className = 'ds-studio-title';
  title.textContent = 'Diatoms';
  const muteAll = document.createElement('button');
  muteAll.className = 'ds-mini';
  muteAll.textContent = 'MUTE ALL';
  muteAll.style.minWidth = '58px';
  muteAll.title = 'mute every diatom';
  muteAll.addEventListener('click', () => {
    for (const e of entries) {
      e.mBtn.classList.add('on-m');
      cb.onMute(e.id, true);
    }
  });
  const unmuteAll = document.createElement('button');
  unmuteAll.className = 'ds-mini';
  unmuteAll.textContent = 'UNMUTE';
  unmuteAll.style.minWidth = '58px';
  unmuteAll.title = 'unmute all + clear solos';
  unmuteAll.addEventListener('click', () => {
    for (const e of entries) {
      e.mBtn.classList.remove('on-m');
      e.sBtn.classList.remove('on-s');
      cb.onMute(e.id, false);
      cb.onSolo(e.id, false);
    }
  });
  const close = document.createElement('button');
  close.className = 'ds-mini';
  close.textContent = '✕';
  close.addEventListener('click', () => panel.classList.add('hidden'));
  head.append(title, muteAll, unmuteAll, close);

  const list = document.createElement('div');
  list.className = 'ds-studio-list';

  for (const d of diatoms) {
    const row = document.createElement('div');
    row.className = 'ds-acc-row';
    rowsById.set(d.id, row);

    const rhead = document.createElement('div');
    rhead.className = 'ds-acc-head';
    const name = document.createElement('div');
    name.className = 'ds-acc-name';
    name.textContent = nameFor(d.id);
    name.addEventListener('click', () => row.classList.toggle('open'));
    const mBtn = document.createElement('button');
    mBtn.className = 'ds-mini';
    mBtn.textContent = 'M';
    mBtn.title = 'mute';
    const sBtn = document.createElement('button');
    sBtn.className = 'ds-mini';
    sBtn.textContent = 'S';
    sBtn.title = 'solo';
    mBtn.addEventListener('click', () => {
      const on = mBtn.classList.toggle('on-m');
      cb.onMute(d.id, on);
    });
    sBtn.addEventListener('click', () => {
      const on = sBtn.classList.toggle('on-s');
      if (on && mBtn.classList.contains('on-m')) {
        // soloing clears mute (both state + UI)
        mBtn.classList.remove('on-m');
        cb.onMute(d.id, false);
      }
      cb.onSolo(d.id, on);
    });
    rhead.append(name, mBtn, sBtn);

    const body = document.createElement('div');
    body.className = 'ds-acc-body';
    // volume
    const volRow = document.createElement('div');
    volRow.className = 'ds-row';
    const volLbl = document.createElement('label');
    volLbl.textContent = 'Vol';
    const vol = document.createElement('input');
    vol.type = 'range';
    vol.min = '0';
    vol.max = '1.5';
    vol.step = '0.01';
    vol.value = '1';
    vol.setAttribute('aria-label', `${nameFor(d.id)} volume`);
    vol.addEventListener('input', () => cb.onGain(d.id, parseFloat(vol.value)));
    volRow.append(volLbl, vol);
    // excitation
    const exRow = document.createElement('div');
    exRow.className = 'ds-row';
    const exLbl = document.createElement('label');
    exLbl.textContent = 'Source';
    const ex = document.createElement('select');
    ex.setAttribute('aria-label', `${nameFor(d.id)} excitation source`);
    for (const [v, t] of [
      ['auto', 'Auto'],
      ['strike', 'Strike'],
      ['bow', 'Bow'],
    ] as const) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = t;
      ex.appendChild(o);
    }
    ex.addEventListener('change', () => cb.onExcitation(d.id, ex.value as Excitation));
    exRow.append(exLbl, ex);

    body.append(volRow, exRow);
    row.append(rhead, body);
    list.appendChild(row);
    entries.push({ id: d.id, row, mBtn, sBtn, vol, ex });
  }

  // footer: save / export / import scenes
  const foot = document.createElement('div');
  foot.className = 'ds-studio-foot';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = 'preset name';
  nameInput.className = 'ds-scene-name';
  nameInput.setAttribute('aria-label', 'preset name');
  const saveBtn = document.createElement('button');
  saveBtn.className = 'ds-mini';
  saveBtn.textContent = 'Save';
  saveBtn.style.minWidth = '44px';
  saveBtn.addEventListener('click', () => cb.onSaveScene(nameInput.value));
  const exportBtn = document.createElement('button');
  exportBtn.className = 'ds-mini';
  exportBtn.textContent = '↧';
  exportBtn.title = 'export scene JSON';
  exportBtn.addEventListener('click', () => cb.onExportScene(nameInput.value));
  const importBtn = document.createElement('button');
  importBtn.className = 'ds-mini';
  importBtn.textContent = '↥';
  importBtn.title = 'import scene JSON';
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'application/json,.json';
  fileInput.style.display = 'none';
  importBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) cb.onImportScene(f);
    fileInput.value = '';
  });
  foot.append(nameInput, saveBtn, exportBtn, importBtn, fileInput);

  panel.append(head, list, foot);

  return {
    el: panel,
    toggle: () => panel.classList.toggle('hidden'),
    isOpen: () => !panel.classList.contains('hidden'),
    reveal: (id: string) => {
      const row = rowsById.get(id);
      if (!row) return;
      for (const r of rowsById.values()) r.classList.remove('open');
      row.classList.add('open');
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      row.animate(
        [{ background: 'rgba(255,255,255,0.18)' }, { background: 'transparent' }],
        { duration: 700 },
      );
    },
    setState: (states) => {
      for (const e of entries) {
        const s = states[e.id];
        if (!s) {
          e.mBtn.classList.remove('on-m');
          e.sBtn.classList.remove('on-s');
          e.vol.value = '1';
          e.ex.value = 'auto';
          continue;
        }
        e.mBtn.classList.toggle('on-m', s.muted);
        e.sBtn.classList.toggle('on-s', s.soloed);
        e.vol.value = String(s.gain);
        e.ex.value = s.excitation;
      }
    },
  };
}
